import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { cn } from "@/lib/utils"

import { Link, useEntry, useProps } from "@/lib/page-rendering"
import { BookOpen, BookUser, Heart, Users } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ActionLink } from "@/components/shared/action-buttons"
import type { SearchResultsPageCapabilities } from "@/types/serializers"

import { SearchPageChrome } from "./page-chrome"
import { Paging, PagingInfo, ResultsPerPage } from "./pagination"
import { useResults } from "./search-context"
import { useSearchStatus } from "@/lib/redux-search"

type SearchIndexComponent =
  | "search/educators/index"
  | "search/classes/index"
  | "search/relief_listings/index"

export function Sidebar({
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  children: React.ReactNode
}) {
  const { wasSearched } = useSearchStatus()

  return (
    <div
      className={cn(
        "flex h-auto w-full flex-col gap-4 p-6 sm:h-full sm:w-1/5 sm:border-r",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "contents divide-y",
          wasSearched ? "visible" : "invisible",
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function Results<Result extends { id: string }>({
  className,
  empty,
  ResultComponent,
  ...props
}: React.ComponentProps<"div"> & {
  empty: React.ReactNode
  ResultComponent: React.ComponentType<{ result: Result }>
}) {
  const results = useResults() as Result[]
  const { wasSearched } = useSearchStatus()

  const { t } = useTranslation("search")
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-5",
        wasSearched ? "visible" : "invisible",
        className,
      )}
      {...props}
    >
      <div className="flex flex-row items-center justify-between">
        <PagingInfo />
        <ResultsPerPage />
      </div>
      {results.length === 0 ? (
        empty
      ) : (
        <ul
          className="grid auto-rows-fr gap-5"
          aria-label={t("results.list.title")}
          data-testid="search-results"
        >
          {results.map((result) => (
            <ResultComponent key={`result-${result.id}`} result={result} />
          ))}
        </ul>
      )}
      <Paging show={5} jump={5} />
    </div>
  )
}

export function SearchTabs() {
  const { t } = useTranslation("search")
  const page = useEntry().name as SearchIndexComponent

  return (
    <Tabs value={page}>
      <TabsList className="h-auto flex-wrap">
        <TabsTrigger value="search/classes/index" className="" asChild>
          <Link href={"/search/classes"}>
            <BookOpen />
            {t("tabs.classes")}
          </Link>
        </TabsTrigger>
        <TabsTrigger value="search/educators/index" className="" asChild>
          <Link href={"/search/educators"}>
            <Users />
            {t("tabs.educators")}
          </Link>
        </TabsTrigger>
        <TabsTrigger value="search/relief_listings/index" className="" asChild>
          <Link href={"/search/relief_listings"}>
            <BookUser />
            {t("tabs.relief_listings")}
          </Link>
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )
}

export function SearchContent({ children }: React.PropsWithChildren) {
  return <CardContent>{children}</CardContent>
}

export function SearchHeader({ children }: React.PropsWithChildren) {
  return (
    <CardHeader>
      <SearchTabs />
      {children}
    </CardHeader>
  )
}

// The props every search results page is rendered with.
export type ResultsPageProps = {
  capabilities: SearchResultsPageCapabilities
}

// The chrome and body of a search index page: the Search root, its only
// crumb, with the way to the shortlists beside it for a principal the
// shortlist policy lets reach them, then the card the tabs sit inside.
export function SearchLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("shortlists")
  const { capabilities } = useProps<ResultsPageProps>()

  return (
    <>
      <SearchPageChrome
        secondary={
          capabilities.can_list_shortlists ? (
            <ActionLink href="/search/shortlists" Icon={Heart}>
              {t("index.title")}
            </ActionLink>
          ) : undefined
        }
      />
      <PageBody>
        <Card>{children}</Card>
      </PageBody>
    </>
  )
}
